/**
 * Image Quality Check Utility
 * Provides real-time image quality analysis for dental photos
 *
 * Quality Metrics:
 * - Blur detection (Laplacian variance)
 * - Brightness analysis
 * - Contrast analysis
 * - Resolution check
 *
 * Quality Score: 0-100
 * - 85-100: Excellent (Green)
 * - 60-84: Acceptable (Yellow/Warning)
 * - 0-59: Poor - Must Retake (Red/Blocked)
 */

/**
 * Analyze image quality
 * @param {string} base64Image - Base64 encoded image data
 * @returns {Promise<Object>} Quality analysis results
 */
export async function analyzeImageQuality(base64Image) {
  return new Promise((resolve, reject) => {
    if (!base64Image) {
      reject(new Error('No image data provided'));
      return;
    }

    const img = new Image();

    img.onload = () => {
      try {
        // Create canvas for analysis
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Perform quality checks
        const resolution = checkResolution(img.width, img.height);
        const brightness = checkBrightness(imageData);
        const contrast = checkContrast(imageData);
        const sharpness = checkSharpness(imageData);

        // Calculate overall quality score
        const qualityScore = calculateQualityScore({
          resolution,
          brightness,
          contrast,
          sharpness
        });

        // Generate recommendations
        const recommendations = generateRecommendations({
          resolution,
          brightness,
          contrast,
          sharpness,
          qualityScore
        });

        resolve({
          score: qualityScore,
          metrics: {
            resolution,
            brightness,
            contrast,
            sharpness
          },
          recommendations,
          passingScore: qualityScore >= 60,
          quality: getQualityLevel(qualityScore)
        });

      } catch (error) {
        reject(new Error(`Quality analysis failed: ${error.message}`));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for quality check'));
    };

    img.src = base64Image;
  });
}

/**
 * Check image resolution
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {Object} Resolution check results
 */
function checkResolution(width, height) {
  const minWidth = 800;
  const minHeight = 600;
  const optimalWidth = 1920;
  const optimalHeight = 1080;

  const megapixels = (width * height) / 1000000;

  let score = 0;
  let status = 'poor';
  let message = '';

  if (width >= optimalWidth && height >= optimalHeight) {
    score = 100;
    status = 'excellent';
    message = 'ความละเอียดดีเยี่ยม';
  } else if (width >= minWidth && height >= minHeight) {
    // Scale score based on how close to optimal
    const widthRatio = width / optimalWidth;
    const heightRatio = height / optimalHeight;
    score = Math.min(100, Math.round(((widthRatio + heightRatio) / 2) * 100));
    score = Math.max(60, score); // Minimum 60 if meets minimum requirements
    status = score >= 85 ? 'good' : 'acceptable';
    message = 'ความละเอียดเพียงพอ';
  } else {
    score = Math.round((Math.min(width / minWidth, height / minHeight)) * 60);
    status = 'poor';
    message = 'ความละเอียดต่ำเกินไป';
  }

  return {
    width,
    height,
    megapixels: megapixels.toFixed(1),
    score,
    status,
    message
  };
}

/**
 * Check image brightness
 * @param {ImageData} imageData - Canvas image data
 * @returns {Object} Brightness check results
 */
function checkBrightness(imageData) {
  const data = imageData.data;
  let sum = 0;

  // Sample every 10th pixel for performance
  for (let i = 0; i < data.length; i += 40) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // Calculate luminance
    sum += (0.299 * r + 0.587 * g + 0.114 * b);
  }

  const avgBrightness = sum / (data.length / 40);

  let score = 0;
  let status = 'poor';
  let message = '';

  // Optimal brightness: 80-180
  if (avgBrightness >= 80 && avgBrightness <= 180) {
    score = 100;
    status = 'excellent';
    message = 'ความสว่างเหมาะสม';
  } else if (avgBrightness >= 60 && avgBrightness <= 200) {
    // Acceptable range
    const deviation = Math.min(
      Math.abs(avgBrightness - 130) / 70
    );
    score = Math.round(100 - (deviation * 40));
    status = 'acceptable';
    message = avgBrightness < 80 ? 'ค่อนข้างมืด' : 'ค่อนข้างสว่าง';
  } else {
    // Poor brightness
    if (avgBrightness < 60) {
      score = Math.round((avgBrightness / 60) * 60);
      status = 'poor';
      message = 'มืดเกินไป';
    } else {
      score = Math.round(Math.max(0, 100 - ((avgBrightness - 200) / 55 * 100)));
      score = Math.min(60, score);
      status = 'poor';
      message = 'สว่างเกินไป';
    }
  }

  return {
    value: Math.round(avgBrightness),
    score,
    status,
    message
  };
}

/**
 * Check image contrast
 * @param {ImageData} imageData - Canvas image data
 * @returns {Object} Contrast check results
 */
function checkContrast(imageData) {
  const data = imageData.data;
  let min = 255;
  let max = 0;

  // Sample every 10th pixel for performance
  for (let i = 0; i < data.length; i += 40) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    if (luminance < min) min = luminance;
    if (luminance > max) max = luminance;
  }

  const contrastRatio = max - min;

  let score = 0;
  let status = 'poor';
  let message = '';

  // Good contrast: 100-200
  if (contrastRatio >= 100 && contrastRatio <= 220) {
    score = 100;
    status = 'excellent';
    message = 'คอนทราสต์ดีเยี่ยม';
  } else if (contrastRatio >= 70) {
    // Acceptable contrast
    score = Math.min(100, Math.round((contrastRatio / 100) * 85));
    score = Math.max(60, score);
    status = 'acceptable';
    message = 'คอนทราสต์เพียงพอ';
  } else {
    // Poor contrast
    score = Math.round((contrastRatio / 70) * 60);
    status = 'poor';
    message = 'คอนทราสต์ต่ำเกินไป';
  }

  return {
    value: Math.round(contrastRatio),
    score,
    status,
    message
  };
}

/**
 * Check image sharpness using Laplacian variance
 * @param {ImageData} imageData - Canvas image data
 * @returns {Object} Sharpness check results
 */
function checkSharpness(imageData) {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;

  // Convert to grayscale and calculate Laplacian variance
  let variance = 0;
  let mean = 0;
  let count = 0;

  // Sample grid for performance (every 4th pixel)
  const step = 4;

  for (let y = step; y < height - step; y += step) {
    for (let x = step; x < width - step; x += step) {
      const idx = (y * width + x) * 4;

      // Get grayscale value
      const center = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];

      // Calculate Laplacian (edge detection)
      const top = 0.299 * data[((y - step) * width + x) * 4] +
                  0.587 * data[((y - step) * width + x) * 4 + 1] +
                  0.114 * data[((y - step) * width + x) * 4 + 2];
      const bottom = 0.299 * data[((y + step) * width + x) * 4] +
                     0.587 * data[((y + step) * width + x) * 4 + 1] +
                     0.114 * data[((y + step) * width + x) * 4 + 2];
      const left = 0.299 * data[(y * width + (x - step)) * 4] +
                   0.587 * data[(y * width + (x - step)) * 4 + 1] +
                   0.114 * data[(y * width + (x - step)) * 4 + 2];
      const right = 0.299 * data[(y * width + (x + step)) * 4] +
                    0.587 * data[(y * width + (x + step)) * 4 + 1] +
                    0.114 * data[(y * width + (x + step)) * 4 + 2];

      const laplacian = Math.abs(-4 * center + top + bottom + left + right);
      mean += laplacian;
      count++;
    }
  }

  mean = mean / count;

  // Calculate variance
  for (let y = step; y < height - step; y += step) {
    for (let x = step; x < width - step; x += step) {
      const idx = (y * width + x) * 4;

      const center = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];

      const top = 0.299 * data[((y - step) * width + x) * 4] +
                  0.587 * data[((y - step) * width + x) * 4 + 1] +
                  0.114 * data[((y - step) * width + x) * 4 + 2];
      const bottom = 0.299 * data[((y + step) * width + x) * 4] +
                     0.587 * data[((y + step) * width + x) * 4 + 1] +
                     0.114 * data[((y + step) * width + x) * 4 + 2];
      const left = 0.299 * data[(y * width + (x - step)) * 4] +
                   0.587 * data[(y * width + (x - step)) * 4 + 1] +
                   0.114 * data[(y * width + (x - step)) * 4 + 2];
      const right = 0.299 * data[(y * width + (x + step)) * 4] +
                    0.587 * data[(y * width + (x + step)) * 4 + 1] +
                    0.114 * data[(y * width + (x + step)) * 4 + 2];

      const laplacian = Math.abs(-4 * center + top + bottom + left + right);
      variance += Math.pow(laplacian - mean, 2);
    }
  }

  variance = variance / count;

  let score = 0;
  let status = 'poor';
  let message = '';

  // Good sharpness: variance > 100
  // Acceptable: variance 30-100
  // Poor: variance < 30

  if (variance >= 100) {
    score = 100;
    status = 'excellent';
    message = 'ภาพคมชัดดี';
  } else if (variance >= 30) {
    score = Math.round((variance / 100) * 100);
    score = Math.max(60, score);
    status = 'acceptable';
    message = 'ภาพคมชัดพอใช้';
  } else {
    score = Math.round((variance / 30) * 60);
    status = 'poor';
    message = 'ภาพเบลอเกินไป';
  }

  return {
    variance: Math.round(variance * 10) / 10,
    score,
    status,
    message
  };
}

/**
 * Calculate overall quality score
 * @param {Object} metrics - All quality metrics
 * @returns {number} Overall quality score (0-100)
 */
function calculateQualityScore(metrics) {
  // Weighted average of all metrics
  // Sharpness is most important for dental analysis
  const weights = {
    sharpness: 0.4,    // 40%
    resolution: 0.25,  // 25%
    brightness: 0.20,  // 20%
    contrast: 0.15     // 15%
  };

  const score =
    (metrics.sharpness.score * weights.sharpness) +
    (metrics.resolution.score * weights.resolution) +
    (metrics.brightness.score * weights.brightness) +
    (metrics.contrast.score * weights.contrast);

  return Math.round(score);
}

/**
 * Get quality level based on score
 * @param {number} score - Quality score
 * @returns {string} Quality level
 */
function getQualityLevel(score) {
  if (score >= 85) return 'excellent';
  if (score >= 60) return 'acceptable';
  return 'poor';
}

/**
 * Generate recommendations based on quality metrics
 * @param {Object} params - Quality metrics and score
 * @returns {Array<string>} List of recommendations
 */
function generateRecommendations({ resolution, brightness, contrast, sharpness, qualityScore }) {
  const recommendations = [];

  if (qualityScore < 60) {
    recommendations.push('⚠️ คุณภาพภาพต่ำเกินไป - กรุณาถ่ายใหม่');
  }

  if (sharpness.status === 'poor') {
    recommendations.push('📸 จับกล้องให้นิ่ง หรือใช้ขาตั้งกล้อง');
  }

  if (brightness.status === 'poor') {
    if (brightness.value < 80) {
      recommendations.push('💡 เพิ่มแสงสว่าง หรือถ่ายในที่ที่มีแสงมากกว่า');
    } else {
      recommendations.push('☀️ ลดแสงสว่าง หรือถ่ายในที่ที่มีแสงน้อยกว่า');
    }
  }

  if (contrast.status === 'poor') {
    recommendations.push('🔆 ปรับมุมกล้องหรือแสงเพื่อเพิ่มคอนทราสต์');
  }

  if (resolution.status === 'poor') {
    recommendations.push('📱 ใช้กล้องที่มีความละเอียดสูงกว่า');
  }

  if (recommendations.length === 0) {
    recommendations.push('✅ คุณภาพภาพดี สามารถใช้งานได้');
  }

  return recommendations;
}

/**
 * Get quality badge variant for Bootstrap
 * @param {number} score - Quality score
 * @returns {string} Bootstrap badge variant
 */
export function getQualityBadgeVariant(score) {
  if (score >= 85) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
}

/**
 * Get quality icon
 * @param {number} score - Quality score
 * @returns {string} Icon class
 */
export function getQualityIcon(score) {
  if (score >= 85) return 'fas fa-check-circle';
  if (score >= 60) return 'fas fa-exclamation-triangle';
  return 'fas fa-times-circle';
}

export default {
  analyzeImageQuality,
  getQualityBadgeVariant,
  getQualityIcon
};
